import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ORDER_PAYMENT_TERMS_LABELS_RU,
  OrderPaymentTerms,
  SELLER_ENTITY_LABELS_RU,
  SellerEntity,
  isSellerEntity,
  needsRopApproval,
} from '@crm/shared'
import type {
  OrderPaymentTerms as OrderPaymentTermsT,
  SellerEntity as SellerEntityT,
} from '@crm/shared'
import { createInvoiceFromRequest } from '../../../shared/api/orders.api'
import { generateInvoiceUnf } from '../utils/generateInvoiceUnf'

interface Props {
  requestId: string
  /** Юрлицо по умолчанию: юрлицо продажи заявки, у «ещё счёта» — юрлицо счетов
   *  заказа. Нет (старая заявка) — СПЕЦМАШЭЛЕКТРО. */
  defaultEntityId?: string | null
  /** Заказ уже есть («Выгрузить ещё счёт»): условия оплаты сервер не меняет —
   *  их правят в карточке заказа, поэтому поля условий не показываем. */
  forExistingOrder?: boolean
  onClose: () => void
  onCreated: (orderId: string) => void
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось выгрузить шаблон счёта'
}

// Очистка ввода до цифр (пустое поле остаётся пустым, без ведущего «0»).
const digits = (s: string) => s.replace(/\D/g, '')

const inputCls = 'w-full rounded border border-gray-300 px-2 py-1.5'

export function CreateInvoiceModal({
  requestId,
  defaultEntityId,
  forExistingOrder = false,
  onClose,
  onCreated,
}: Props) {
  const qc = useQueryClient()
  const [paymentTermsType, setPaymentTermsType] = useState<OrderPaymentTermsT>('PREPAYMENT')
  const [prepayStr, setPrepayStr] = useState('100')
  const [daysStr, setDaysStr] = useState('')
  const [vatStr, setVatStr] = useState('22')
  const [entityId, setEntityId] = useState<SellerEntityT>(
    isSellerEntity(defaultEntityId) ? defaultEntityId : SellerEntity.SPM,
  )
  const [error, setError] = useState<string | null>(null)

  const isPrepayment = paymentTermsType === 'PREPAYMENT'
  const isDeferred = paymentTermsType === 'DEFERRED' || paymentTermsType === 'POSTPAYMENT'
  // Плановая предоплата для расчёта согласования: % только при «Предоплате»,
  // иначе 0 (гарантийное письмо/отсрочка/постоплата → согласование РОП).
  const prepaymentPercent = isPrepayment ? Math.min(100, Number(prepayStr || 0)) : 0
  const approval = needsRopApproval(prepaymentPercent)

  const mutation = useMutation({
    mutationFn: () =>
      createInvoiceFromRequest(requestId, {
        paymentTermsType,
        prepaymentPercent,
        deferralDays: isDeferred ? (daysStr ? Number(daysStr) : null) : null,
        vatRate: Math.min(100, Number(vatStr || 0)),
        entityId,
      }),
    onSuccess: async (res) => {
      await generateInvoiceUnf(res.template)
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['orders', res.order.id] })
      onCreated(res.order.id)
    },
    onError: (e) => setError(extractError(e)),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">Выгрузить шаблон для счёта в УНФ</h2>
        <p className="mb-4 text-sm text-gray-500">
          {forExistingOrder
            ? 'Добавит в заказ счёт по позициям «Счёт», которых ещё нет в счетах, и сформирует Excel-шаблон. Условия оплаты заказа не меняются — их правят в карточке заказа.'
            : 'Создаст заказ по позициям в статусе «Счёт» и сформирует Excel-шаблон.'}
        </p>

        <label className={forExistingOrder ? 'hidden' : 'mb-3 block text-sm'}>
          <span className="mb-1 block font-medium text-gray-700">Условие оплаты</span>
          <select
            className={inputCls}
            value={paymentTermsType}
            onChange={(e) => setPaymentTermsType(e.target.value as OrderPaymentTermsT)}
          >
            {Object.values(OrderPaymentTerms).map((t) => (
              <option key={t} value={t}>
                {ORDER_PAYMENT_TERMS_LABELS_RU[t]}
              </option>
            ))}
          </select>
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          {/* Поле зависит от условия оплаты: предоплата % / дни отсрочки / — */}
          {forExistingOrder ? (
            <div />
          ) : isPrepayment ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Предоплата, %</span>
              <input
                type="text"
                inputMode="numeric"
                className={inputCls}
                value={prepayStr}
                onChange={(e) => setPrepayStr(digits(e.target.value).slice(0, 3))}
              />
            </label>
          ) : isDeferred ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {paymentTermsType === 'DEFERRED' ? 'Отсрочка, дней' : 'Постоплата, дней'}
              </span>
              <input
                type="text"
                inputMode="numeric"
                className={inputCls}
                placeholder="напр. 30"
                value={daysStr}
                onChange={(e) => setDaysStr(digits(e.target.value).slice(0, 4))}
              />
            </label>
          ) : (
            <div className="flex items-end text-xs text-gray-500">
              По гарантийному письму предоплата не требуется.
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">НДС, %</span>
            <input
              type="text"
              inputMode="numeric"
              className={inputCls}
              value={vatStr}
              onChange={(e) => setVatStr(digits(e.target.value).slice(0, 3))}
            />
          </label>
        </div>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Юрлицо-продавец</span>
          <select
            className={inputCls}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value as SellerEntityT)}
          >
            {Object.values(SellerEntity).map((id) => (
              <option key={id} value={id}>
                {SELLER_ENTITY_LABELS_RU[id]}
              </option>
            ))}
          </select>
        </label>

        {approval && !forExistingOrder && (
          <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Плановая предоплата меньше 50% — заказ уйдёт на согласование РОП. Шаблон счёта
            выгрузится сразу.
          </p>
        )}
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Отмена
          </button>
          <button
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={() => {
              setError(null)
              mutation.mutate()
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Формирую…' : 'Выгрузить шаблон'}
          </button>
        </div>
      </div>
    </div>
  )
}
